import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '@/services';
import { usePermissions } from '../hooks/usePermissions';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { getResolvedPortalTitle, getResolvedSupportEmail } from '../utils/portalBranding';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';

const MemberSupport = () => {
  const { t } = useTranslation('common');
  const { settings } = useSettings();
  const { hasPermission, role } = usePermissions();
  const { user } = useAuth();
  const supportEmail = getResolvedSupportEmail(settings);
  const portalTitle = getResolvedPortalTitle(settings);
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const canViewAssistance =
    hasPermission('contributions.view_own') || hasPermission('contributions.view');
  const [personalStats, setPersonalStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await dashboardAPI.getPersonalStats();
        const payload = res?.data;
        if (!cancelled) setPersonalStats(payload || null);
      } catch (e) {
        console.error('MemberSupport personal stats:', e);
        if (!cancelled) setPersonalStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const memberRef = personalStats?.member_id ?? '-';
  const demandesCount =
    personalStats?.pending_requests_count ?? personalStats?.unpaid_count ?? 0;

  return (
    <div className="mx-auto max-w-lg space-y-3 pb-6 md:max-w-2xl md:space-y-4">
      {isMembre ? (
        <MemberMobileShell>
          <MemberMobileHero
            label={t('memberSupportPage.kicker')}
            title={t('memberSupportPage.introTitle')}
            hint={loading ? '…' : t('memberSupportPage.allocationsCount', { count: demandesCount })}
            tone={demandesCount > 0 ? 'warn' : 'info'}
            actions={
              <>
                {canViewAssistance ? (
                  <Link
                    to="/assistance"
                    className="mm-cta flex-1"
                  >
                    {t('memberSupportPage.goAssistance', { defaultValue: 'Allocations' })}
                  </Link>
                ) : null}
                {supportEmail ? (
                  <a
                    href={`mailto:${supportEmail}`}
                    className="mm-cta mm-cta--ghost flex-1"
                  >
                    {t('memberSupportPage.emailCta', { defaultValue: 'Écrire' })}
                  </a>
                ) : null}
              </>
            }
          >
            <p className="mt-2 text-xs text-slate-600">
              {t('memberSupportPage.memberRef')} ·{' '}
              <span className="font-semibold tabular-nums text-[#0f3350]">{memberRef}</span>
            </p>
          </MemberMobileHero>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/documents"
              className="mm-press mm-card px-3 py-3 text-center text-xs font-bold text-[#0f3350]"
            >
              {t('memberSupportPage.linkDocuments')}
            </Link>
            <Link
              to="/notifications"
              className="mm-press mm-card px-3 py-3 text-center text-xs font-bold text-[#0f3350]"
            >
              {t('memberSupportPage.linkAnnouncements')}
            </Link>
            <Link
              to="/my-fund"
              className="mm-press mm-card px-3 py-3 text-center text-xs font-bold text-[#0f3350]"
            >
              {t('memberSupportPage.linkOrg')}
            </Link>
            {canViewAssistance ? (
              <Link
                to="/assistance"
                className="mm-press mm-card px-3 py-3 text-center text-xs font-bold text-[#0f3350]"
              >
                {t('memberSupportPage.linkAllocations')}
              </Link>
            ) : null}
          </div>
        </MemberMobileShell>
      ) : null}

      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 md:p-6">
        <div className={isMembre ? 'hidden md:block' : ''}>
          <h1 className="text-xl font-semibold text-[#0f3350] md:text-2xl">{t('memberSupportPage.pageTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600 md:text-base">{t('memberSupportPage.pageDesc')}</p>
        </div>
        {loading ? (
          <div className={`flex justify-center py-8 ${isMembre ? 'hidden md:flex mt-4 md:mt-6' : 'mt-6'}`}>
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#2f6fd4] border-t-transparent" />
          </div>
        ) : (
          <dl className={`space-y-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-sm md:text-base ${isMembre ? 'hidden md:block mt-4 md:mt-5' : 'mt-5'}`}>
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
              <dt className="font-medium text-slate-700">{t('memberSupportPage.memberRef')}</dt>
              <dd className="font-semibold text-[#0f3350] tabular-nums">{memberRef}</dd>
            </div>
            <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
              <dt className="font-medium text-slate-700">{t('memberSupportPage.allocationsInProgress')}</dt>
              <dd className="text-slate-800">{t('memberSupportPage.allocationsCount', { count: demandesCount })}</dd>
            </div>
            <div className="flex flex-col gap-0.5 pt-1">
              <dt className="font-medium text-slate-700">{t('memberSupportPage.emailSupport')}</dt>
              <dd className="text-slate-800 break-all">{supportEmail || '—'}</dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="font-medium text-slate-700">{t('memberSupportPage.portal')}</dt>
              <dd className="text-slate-800">{portalTitle}</dd>
            </div>
          </dl>
        )}
        {canViewAssistance ? (
          <div className={`mt-4 ${isMembre ? 'hidden md:block' : ''}`}>
            <Link
              to="/assistance"
              className="mm-cta"
            >
              {t('memberSupportPage.goAssistance', { defaultValue: 'Voir mes allocations' })}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MemberSupport;
